import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { LogisticConfiguration, Partner } from "@dashboard/common/types";
import { actions, partnerSelector, urlShareSelector } from "@dashboard/config/redux";
import { getEnvVar } from "@dashboard/common/utils";
import { fetchUpdateUserDetailForDelivery } from "../fetchers/delivery-config.fetchers";
import { mergeUserDetailAfterSave } from "../utils/user-detail.utils";
import { useDeliveryToken } from "./use-delivery-token.hook";

type CoverageView = "select" | "local" | "national";

export const useDeliveryCoverage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const storeUrlShare = useSelector(urlShareSelector);
  const [view, setView] = useState<CoverageView>("select");
  const [localEnabled, setLocalEnabled] = useState(false);
  const [nationalEnabled, setNationalEnabled] = useState(false);
  const [cities, setCities] = useState<string[]>([]);
  const [cityInput, setCityInput] = useState("");
  const [loading, setLoading] = useState(false);

  const userId = partner?.userId;
  const countryCities = partner?.userCountry?.cities || [];

  useEffect(() => {
    const lc = partner?.userDetail?.logisticConfiguration;
    if (!lc) return;
    setLocalEnabled(!!lc.localLogisticEnabled);
    setNationalEnabled(!!lc.nationalLogisticEnabled);
    setCities(lc.localAvailabilityCities || []);
  }, [partner?.userDetail]);

  const storeUrl = useMemo(() => {
    if (!partner) return getEnvVar("REACT_APP_HOST");
    const catalogueIdentifier =
      partner.userNetwork?.urlSummer || partner.userId;
    return (
      storeUrlShare?.url ||
      `${getEnvVar("REACT_APP_HOST")}/catalogo/${catalogueIdentifier}`
    );
  }, [partner, storeUrlShare]);

  const addCity = useCallback(() => {
    const trimmed = cityInput.trim();
    if (!trimmed || cities.includes(trimmed)) return;
    setCities((prev) => [...prev, trimmed]);
    setCityInput("");
  }, [cityInput, cities]);

  const removeCity = useCallback((city: string) => {
    setCities((prev) => prev.filter((c) => c !== city));
  }, []);

  const handleLocalToggle = useCallback((enabled: boolean) => {
    if (enabled && cities.length === 0) {
      toast.error(t("delivery.coverage.noCities"));
      return;
    }
    setLocalEnabled(enabled);
  }, [cities.length, t]);

  const handleNationalToggle = useCallback((enabled: boolean) => {
    setNationalEnabled(enabled);
    if (enabled) {
      setLocalEnabled(false);
      setCities([]);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!partner?.userDetail || !token) return;
    if (localEnabled && cities.length === 0) {
      toast.error(t("delivery.coverage.noCities"));
      return;
    }
    setLoading(true);
    try {
      const logisticConfiguration: LogisticConfiguration = {
        ...partner.userDetail.logisticConfiguration,
        localLogisticEnabled: localEnabled,
        nationalLogisticEnabled: nationalEnabled,
        localAvailabilityCities: nationalEnabled ? [] : cities,
      };
      const payload: Partner = {
        ...partner.userDetail,
        logisticConfiguration,
      };
      const updated = await fetchUpdateUserDetailForDelivery(token, payload);
      const mergedUserDetail = mergeUserDetailAfterSave(
        partner.userDetail,
        payload,
        updated
      );
      dispatch(actions.setUserDetailData({ userDetail: mergedUserDetail }));
      toast.success(t("delivery.coverage.saveSuccess"));
    } catch {
      toast.error(t("delivery.settings.saveError"));
    } finally {
      setLoading(false);
    }
  }, [
    partner,
    token,
    localEnabled,
    nationalEnabled,
    cities,
    dispatch,
    t,
  ]);

  const openPreview = useCallback(() => {
    if (!partner) return;
    window.open(storeUrl, "_blank");
  }, [partner, storeUrl]);

  const breadcrumbs = useMemo(
    () => [
      { label: t("delivery.breadcrumb.delivery"), link: `/${userId}/delivery` },
      { label: t("delivery.breadcrumb.coverage") },
    ],
    [t, userId]
  );

  const filteredCities = useMemo(() => {
    if (!cityInput.trim()) return countryCities.slice(0, 20);
    const q = cityInput.toLowerCase();
    return countryCities.filter((c) => c.toLowerCase().includes(q)).slice(0, 20);
  }, [countryCities, cityInput]);

  return {
    view,
    setView,
    localEnabled,
    nationalEnabled,
    cities,
    cityInput,
    setCityInput,
    loading,
    addCity,
    removeCity,
    handleLocalToggle,
    handleNationalToggle,
    handleSave,
    openPreview,
    breadcrumbs,
    filteredCities,
  };
};
